// src/design-system/v2/simbologia.ts

/**
 * Simbología cartográfica PTEL — módulo de datos (v6.1).
 *
 * Catálogo 19/19 de las tipologías de infraestructuras críticas: contenedor
 * (familia), glifo, color de identidad y derivados. Datos puros, sin montar
 * en UI (el render lo hará P2 Valida). Insumo congelado en
 * `.ptel/DECISION_SIMBOLOGIA_v6_20260705.md`.
 *
 * - 17 glifos Phosphor (peso `fill`): se guarda `ref` + `bbox` medido; el
 *   consumidor renderiza el componente Phosphor por nombre.
 * - 2 glifos propios (BOMBEROS casco frontal, CULTURAL fachada): `svgPaths`
 *   inline (rejilla 256) + `bbox`, sin dependencia de librería.
 *
 * Dimensionado (§4 de la decisión): normalización por caja real (bbox),
 * escalado al objetivo de la familia y centrado por márgenes simétricos.
 *
 * BOMBEROS (glifo) y los objetivos de caja segura son provisionales, sujetos
 * a validación sobre ortofoto PNOA en P2-S2.
 *
 * @module design-system/v2/simbologia
 */

import type { TipologiaPTEL } from '../../types/tipologia';

/** Familia de contenedor (forma) → categoría macro. */
export type FamiliaSimbologia = 'rombo' | 'cuadrado' | 'hexagono' | 'circulo' | 'punteado';

/** Glifo interior de una tipología. */
export interface GlifoSimbologia {
  /** Origen del glifo. */
  fuente: 'phosphor' | 'propio';
  /** Nombre del icono Phosphor (solo `fuente === 'phosphor'`). */
  ref?: string;
  /** Markup SVG interior en rejilla 256 (solo `fuente === 'propio'`). */
  svgPaths?: string;
  /** Caja delimitadora real del dibujo `[x0, y0, x1, y1]` (unidades 256). */
  bbox: readonly [number, number, number, number];
}

/** Entrada de simbología de una tipología. */
export interface SimbologiaTipologia {
  clave: TipologiaPTEL;
  familia: FamiliaSimbologia;
  glifo: GlifoSimbologia;
  /** Color de identidad (hex), validado C1 (contraste ≥ 3:1) y C2 (ΔE ≥ 25). */
  colorIdentidad: string;
  /** Fondo suave derivado (rgba con alfa 0.16) para chips y badges. */
  colorSoft: string;
  /** Variable CSS canónica de la tipología (`--typ-<clave>`). */
  cssVar: string;
}

/**
 * Objetivo de caja segura por familia: fracción del lado del marcador (S) que
 * ocupa el lado mayor del bbox del glifo. Común a todos los glifos de una
 * misma forma → tamaño coherente entre hermanos. Valores tuneables en P2-S2.
 */
export const OBJETIVO_CAJA_SEGURA: Record<FamiliaSimbologia, number> = {
  rombo: 0.46,
  cuadrado: 0.6,
  hexagono: 0.54,
  circulo: 0.5,
  punteado: 0.48,
};

/** Deriva el fondo suave rgba desde un color hex. */
function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Constructor de entrada: deriva `colorSoft` y `cssVar` desde el color y la clave. */
function entrada(
  clave: TipologiaPTEL,
  familia: FamiliaSimbologia,
  colorIdentidad: string,
  glifo: GlifoSimbologia,
): SimbologiaTipologia {
  return {
    clave,
    familia,
    glifo,
    colorIdentidad,
    colorSoft: hexToRgba(colorIdentidad, 0.16),
    cssVar: `--typ-${clave.toLowerCase()}`,
  };
}

// —— Glifos propios (markup rejilla 256, sin dependencia de librería) ——

/** BOMBEROS · casco frontal (cresta central + banda + emblema). Provisional. */
const CASCO_BOMBEROS =
  '<path d="M112 56 L128 16 L144 56 Z"/><path fill-rule="evenodd" d="M46 150 Q46 62 128 56 Q210 62 210 150 Z M106 62 h6 v82 h-6 Z M144 62 h6 v82 h-6 Z M50 116 h56 v13 h-56 Z M150 116 h56 v13 h-56 Z"/><path d="M14 158 Q128 142 242 158 Q242 194 128 202 Q14 194 14 158 Z"/><path fill-rule="evenodd" d="M108 113 h40 v19 h-40 Z M128 122.5 m-7 0 a7 7 0 1 0 14 0 a7 7 0 1 0 -14 0 Z"/>';

/** CULTURAL · fachada clásica (frontón + columnas + base). */
const FACHADA_CULTURAL =
  '<path d="M128 22 240 80H16Z"/><rect x="28" y="90" width="200" height="16" rx="4"/><rect x="44" y="116" width="24" height="84" rx="4"/><rect x="94" y="116" width="24" height="84" rx="4"/><rect x="140" y="116" width="24" height="84" rx="4"/><rect x="188" y="116" width="24" height="84" rx="4"/><rect x="20" y="210" width="216" height="24" rx="6"/>';

/**
 * Catálogo de simbología 19/19. Color validado C1/C2 y bbox medido con
 * `svg-path-bbox` (script `.ptel/p0_s1bimpl_20260706/gen_datos_simbologia.mjs`).
 */
export const SIMBOLOGIA: Record<TipologiaPTEL, SimbologiaTipologia> = {
  // Intervención y emergencias (rombo)
  SANITARIO: entrada('SANITARIO', 'rombo', '#E91E63', { fuente: 'phosphor', ref: 'FirstAid', bbox: [24, 24, 232, 232] }),
  POLICIAL: entrada('POLICIAL', 'rombo', '#3F51B5', { fuente: 'phosphor', ref: 'ShieldCheck', bbox: [32, 40, 224, 240] }),
  BOMBEROS: entrada('BOMBEROS', 'rombo', '#DE7C00', { fuente: 'propio', svgPaths: CASCO_BOMBEROS, bbox: [14, 16, 242, 202] }),
  EMERGENCIAS: entrada('EMERGENCIAS', 'rombo', '#F44336', { fuente: 'phosphor', ref: 'BellSimpleRinging', bbox: [28, 16, 228, 232] }),
  // Equipamientos y servicios (cuadrado)
  EDUCATIVO: entrada('EDUCATIVO', 'cuadrado', '#2196F3', { fuente: 'phosphor', ref: 'GraduationCap', bbox: [0, 24, 256, 248] }),
  CULTURAL: entrada('CULTURAL', 'cuadrado', '#9C27B0', { fuente: 'propio', svgPaths: FACHADA_CULTURAL, bbox: [16, 22, 240, 234] }),
  RELIGIOSO: entrada('RELIGIOSO', 'cuadrado', '#795548', { fuente: 'phosphor', ref: 'Church', bbox: [24, 8, 232, 224] }),
  DEPORTIVO: entrada('DEPORTIVO', 'cuadrado', '#49A84D', { fuente: 'phosphor', ref: 'CourtBasketball', bbox: [16, 48, 240, 208] }),
  SOCIAL: entrada('SOCIAL', 'cuadrado', '#D07C00', { fuente: 'phosphor', ref: 'UsersThree', bbox: [8, 48, 248, 224] }),
  MUNICIPAL: entrada('MUNICIPAL', 'cuadrado', '#607D8B', { fuente: 'phosphor', ref: 'Building', bbox: [16, 16, 240, 240] }),
  // Infraestructura técnica (hexágono lado plano)
  COMBUSTIBLE: entrada('COMBUSTIBLE', 'hexagono', '#92400E', { fuente: 'phosphor', ref: 'ChargingStation', bbox: [24, 32, 248, 224] }),
  HIDRAULICO: entrada('HIDRAULICO', 'hexagono', '#05A1BC', { fuente: 'phosphor', ref: 'DropHalf', bbox: [40, 8, 216, 232] }),
  ENERGIA: entrada('ENERGIA', 'hexagono', '#C88109', { fuente: 'phosphor', ref: 'Lightning', bbox: [40, 8, 216, 248] }),
  TELECOM: entrada('TELECOM', 'hexagono', '#7C3AED', { fuente: 'phosphor', ref: 'CellTower', bbox: [48, 8, 208, 240] }),
  RSU: entrada('RSU', 'hexagono', '#65A30D', { fuente: 'phosphor', ref: 'Recycle', bbox: [16, 16, 240, 240] }),
  INDUSTRIAL: entrada('INDUSTRIAL', 'hexagono', '#78716C', { fuente: 'phosphor', ref: 'Factory', bbox: [16, 16, 240, 224] }),
  // Movilidad y transporte (círculo)
  VIAL: entrada('VIAL', 'circulo', '#0D9488', { fuente: 'phosphor', ref: 'RoadHorizon', bbox: [16, 56, 240, 200] }),
  PUERTO: entrada('PUERTO', 'circulo', '#0369A1', { fuente: 'phosphor', ref: 'Sailboat', bbox: [8, 0, 248, 224] }),
  // Sin clasificar (círculo discontinuo)
  GENERICO: entrada('GENERICO', 'punteado', '#6B7280', { fuente: 'phosphor', ref: 'MapPin', bbox: [40, 16, 216, 240] }),
};

/** Resultado de dimensionar un glifo dentro de un marcador de lado `S`. */
export interface GlifoDimensionado {
  /** Ancho renderizado del glifo (px). */
  rw: number;
  /** Alto renderizado del glifo (px). */
  rh: number;
  /** Desplazamiento X para centrar el glifo en el marcador (px). */
  gx: number;
  /** Desplazamiento Y para centrar el glifo en el marcador (px). */
  gy: number;
  /** `viewBox` del sub-SVG del glifo (recorta el bbox). */
  viewBox: string;
}

/**
 * Dimensiona un glifo por su caja real: escala el lado mayor del bbox al
 * objetivo de la familia y lo centra por márgenes simétricos (superior =
 * inferior, izquierdo = derecho). No aplica multiplicador óptico.
 *
 * @param bbox Caja real del glifo `[x0, y0, x1, y1]` (unidades 256).
 * @param familia Familia del contenedor (determina el objetivo).
 * @param S Lado del marcador en px.
 */
export function dimensionaGlifo(
  bbox: readonly [number, number, number, number],
  familia: FamiliaSimbologia,
  S: number,
): GlifoDimensionado {
  const [x0, y0, x1, y1] = bbox;
  const bw = x1 - x0;
  const bh = y1 - y0;
  const k = (OBJETIVO_CAJA_SEGURA[familia] * S) / Math.max(bw, bh);
  const rw = bw * k;
  const rh = bh * k;
  return {
    rw,
    rh,
    gx: (S - rw) / 2,
    gy: (S - rh) / 2,
    viewBox: `${x0} ${y0} ${bw} ${bh}`,
  };
}
